/**
 * Stage 1: the Scout job — pick one that exists, or have Scout's agent build a
 * new one (doc 07, screen 3).
 */

import { useState } from "react";
import { ExternalLink, Hammer } from "lucide-react";

import {
  AgentPanel,
  Banner,
  Button,
  Disclosure,
  Field,
  FieldRow,
  Input,
  JobPicker,
  Select,
  Textarea,
} from "@/components/kit";
import {
  buildScraper,
  decideJobApproval,
  getScoutJob,
  isScoutDown,
  listScoutJobs,
  readJobApprovals,
  readJobChat,
  scoutJobUrl,
  sendJobChat,
  type Approval,
  type ScoutJob,
} from "@/lib/integrations/api";
import type { ImportFieldInfo } from "@/lib/integrations/api";
import { ago, scoutJobStatus } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useEvery, useKeyed } from "../hooks";

export type ImportChoice = { command: string; label: string; fields: ImportFieldInfo[] };

function BuildScraper({
  base,
  imports,
  command: startCommand,
  onCancel,
  onReady,
}: {
  base: string;
  imports: ImportChoice[];
  command: string;
  onCancel: () => void;
  onReady: (job: ScoutJob) => void;
}) {
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [command, setCommand] = useState(startCommand);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const chosen = imports.find((one) => one.command === command);

  const job = useKeyed(jobId ? `${base}|${jobId}` : null, () => getScoutJob(base, jobId ?? ""));
  const chat = useKeyed(jobId ? `${base}|${jobId}|chat` : null, () => readJobChat(base, jobId ?? ""));
  const approvals = useKeyed(jobId ? `${base}|${jobId}|approvals` : null, () => readJobApprovals(base, jobId ?? ""));
  const passed = job.data?.lastPassed ?? null;
  useEvery(Boolean(jobId) && !passed, 5000, () => {
    job.reload();
    chat.reload();
    approvals.reload();
  });

  const start = () => {
    setBusy("start");
    setProblem(null);
    buildScraper({ scoutBaseUrl: base, name: name.trim(), websiteUrl: website.trim(), notes: notes.trim(), importCommand: command || null }).then(
      (made) => setJobId(made.jobId),
      (thrown) =>
        setProblem(
          isScoutDown(thrown)
            ? `Scout is not answering (${errorText(thrown, "no answer")}). Nothing was made; try again when Scout is back.`
            : errorText(thrown, "Scout did not start building"),
        ),
    ).finally(() => setBusy(null));
  };

  const decide = (approval: Approval, decision: { approved?: boolean; answer?: string }) => {
    setBusy(approval.id);
    decideJobApproval(base, approval.id, decision)
      .then(() => approvals.reload(), (thrown) => setProblem(errorText(thrown, "Scout did not take the answer")))
      .finally(() => setBusy(null));
  };

  if (!jobId) {
    return (
      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>Build a new Scout job</strong>
        <FieldRow>
          <Field label="Website address" required htmlFor="build-website">
            <Input id="build-website" value={website} placeholder="https://back.results.asiangames2026.org/…" onChange={(event) => setWebsite(event.target.value)} />
          </Field>
          <Field label="Name" required htmlFor="build-name">
            <Input id="build-name" value={name} placeholder="Asian Games 2026 — medallists by day" onChange={(event) => setName(event.target.value)} />
          </Field>
        </FieldRow>
        <Field label="What it is for" hint="Its fields become Scout's instructions and checklist, so the agent is told exactly what to find." htmlFor="build-for">
          <Select id="build-for" value={command} onChange={(event) => setCommand(event.target.value)}>
            <option value="">Nothing yet — I will say what to collect</option>
            {imports.map((one) => (
              <option key={one.command} value={one.command}>
                {one.label}
              </option>
            ))}
          </Select>
        </Field>
        {chosen && (
          <p style={{ margin: 0, color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
            What to collect, filled from {chosen.label}: {chosen.fields.map((field) => field.label).join(", ")}.
          </p>
        )}
        <Field label="Notes for Scout" hint="In plain words, e.g. one row per match; include results when they exist." htmlFor="build-notes">
          <Textarea id="build-notes" rows={3} style={{ height: "auto" }} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
          Address rotation, pace and browser are set on the job in Scout.
        </p>
        {problem && <Banner tone="danger">{problem}</Banner>}
        <div style={{ display: "flex", gap: "var(--s2)", flexWrap: "wrap" }}>
          <Button
            tone="primary"
            onClick={start}
            disabled={busy !== null || !website.trim() || !name.trim() || (!command && !notes.trim())}
          >
            <Hammer size={15} aria-hidden />
            {busy === "start" ? "Starting…" : "Start building"}
          </Button>
          <Button tone="subtle" onClick={onCancel} disabled={busy !== null}>
            Back to the job list
          </Button>
        </div>
      </div>
    );
  }

  const state = job.data ? scoutJobStatus(job.data.status) : null;
  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      <strong>Scout is building {name}</strong>
      <p style={{ margin: 0, color: "var(--ink-2)" }}>
        {state ? `Job state: ${state.word}.` : "Reading the job…"} Leaving this page does not stop the agent.
      </p>
      {passed && (
        <Banner
          tone="good"
          title="Test run passed."
          actions={
            <Button tone="primary" onClick={() => job.data && onReady(job.data)}>
              Continue with this job
            </Button>
          }
        >
          {passed.rows.toLocaleString()} rows, checklist {Math.round((passed.score ?? 0) * 100)}%.
        </Banner>
      )}
      {problem && <Banner tone="danger">{problem}</Banner>}
      <AgentPanel
        messages={chat.data}
        approvals={approvals.data}
        loading={chat.busy || approvals.busy}
        error={chat.error ?? approvals.error ?? null}
        busy={busy}
        stuck={job.data?.status === "needs_attention" ? "The job needs attention." : null}
        scoutUrl={scoutJobUrl(jobId, true)}
        onRetry={() => {
          chat.reload();
          approvals.reload();
          job.reload();
        }}
        onReply={(text) => {
          setBusy("reply");
          sendJobChat(base, jobId, text)
            .then(() => chat.reload(), (thrown) => setProblem(errorText(thrown, "Scout did not take the message")))
            .finally(() => setBusy(null));
        }}
        onDecide={decide}
      />
    </div>
  );
}

export function SourceStage({
  base,
  onBase,
  job,
  onJob,
  imports,
  command,
  disabled = false,
}: {
  base: string;
  onBase: (base: string) => void;
  job: ScoutJob | null;
  onJob: (job: ScoutJob) => void;
  imports: ImportChoice[];
  command: string;
  disabled?: boolean;
}) {
  const [building, setBuilding] = useState(false);
  const jobs = useKeyed(`${base}|jobs`, () => listScoutJobs(base));

  return (
    <div style={{ display: "grid", gap: "var(--s4)" }}>
      {jobs.error && (
        <Banner
          tone="warning"
          title="Scout is not answering."
          actions={<Button onClick={jobs.reload}>Try again</Button>}
        >
          Settings can still be edited; runs, jobs and previews that need Scout wait. ({jobs.error})
        </Banner>
      )}
      {job && (
        <Banner
          tone={job.lastPassed ? "good" : "warning"}
          title={`Picked: ${job.name}.`}
          actions={
            <a href={scoutJobUrl(job.id)} target="_blank" rel="noreferrer">
              Open in Scout <ExternalLink size={13} aria-hidden />
            </a>
          }
        >
          {job.lastPassed
            ? `Its newest passed run has ${job.lastPassed.rows.toLocaleString()} rows (${ago(job.lastPassed.startedAt)}).`
            : "This job has no passed run to learn from. Run it in Scout, then read the jobs again."}
          {job.usedBy.length > 0 && ` Also used by ${job.usedBy.join(", ")}.`}
        </Banner>
      )}
      {building ? (
        <BuildScraper
          base={base}
          imports={imports}
          command={command}
          onCancel={() => setBuilding(false)}
          onReady={(ready) => {
            setBuilding(false);
            jobs.reload();
            onJob(ready);
          }}
        />
      ) : (
        <JobPicker
          jobs={jobs.data}
          loading={jobs.busy}
          error={jobs.error}
          selected={job?.id ?? null}
          onPick={(picked) => !disabled && onJob(picked)}
          onBuild={() => setBuilding(true)}
          onRetry={jobs.reload}
        />
      )}
      <Disclosure title="Scout's address" note="Where omnium reaches Scout. Most integrations use the same one.">
        <FieldRow>
          <Field label="Scout address" htmlFor="scout-address">
            <Input id="scout-address" value={base} disabled={disabled} onChange={(event) => onBase(event.target.value.trim())} />
          </Field>
        </FieldRow>
      </Disclosure>
    </div>
  );
}
